import React, { useState } from 'react';
import { Download, Users, CreditCard, Heart, FileSpreadsheet, Loader2, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminDataExport = () => {
  const [exporting, setExporting] = useState({});
  const [success, setSuccess] = useState({});
  const [error, setError] = useState(null);

  const exportOptions = [
    {
      id: 'users',
      title: 'User Data',
      description: 'Export all registered users with profile information',
      icon: Users,
      endpoint: '/api/admin/phase2/export/users',
      filename: 'users_export.csv'
    },
    {
      id: 'payments',
      title: 'Payment Records',
      description: 'Export all payment transactions and subscription data',
      icon: CreditCard,
      endpoint: '/api/admin/phase2/export/payments',
      filename: 'payments_export.csv'
    },
    {
      id: 'interests',
      title: 'Interests & Connections',
      description: 'Export all interest requests and mutual connections',
      icon: Heart,
      endpoint: '/api/admin/phase2/export/interests',
      filename: 'interests_export.csv'
    }
  ];

  const handleExport = async (option) => {
    const token = localStorage.getItem('token');
    setExporting(prev => ({ ...prev, [option.id]: true }));
    setError(null);
    setSuccess(prev => ({ ...prev, [option.id]: false }));

    try {
      const response = await axios.get(`${API_URL}${option.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = option.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(prev => ({ ...prev, [option.id]: true }));
      setTimeout(() => setSuccess(prev => ({ ...prev, [option.id]: false })), 3000);
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.detail || 'Failed to export data');
    } finally {
      setExporting(prev => ({ ...prev, [option.id]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-data-export">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Data Export</h1>
          <p className="text-gray-600 mt-1">Download your data in CSV format for analysis or backup</p>
        </div>
        <FileSpreadsheet className="w-10 h-10 text-mint-500" />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {exportOptions.map((option) => (
          <div
            key={option.id}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            data-testid={`export-card-${option.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-mint-50 rounded-lg">
                <option.icon className="w-6 h-6 text-mint-600" />
              </div>
              {success[option.id] && (
                <div className="flex items-center gap-1 text-green-600 text-sm">
                  <Check className="w-4 h-4" />
                  Downloaded
                </div>
              )}
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">{option.title}</h3>
            <p className="text-gray-600 text-sm mb-4">{option.description}</p>

            <button
              onClick={() => handleExport(option)}
              disabled={exporting[option.id]}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid={`export-btn-${option.id}`}
            >
              {exporting[option.id] ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Export CSV
                </>
              )}
            </button>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Export Information</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• All exports are in CSV format compatible with Excel, Google Sheets, etc.</li>
          <li>• Sensitive data like passwords is automatically excluded</li>
          <li>• Large exports may take a few seconds to generate</li>
          <li>• For compliance, all exports are logged in the activity log</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminDataExport;
